import React from 'react';
import { setNodeDragImage, GHOST_CENTERED } from '../../utils/dragGhost';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import classNames from 'classnames';
import { typeLabelOf, shapeClassOf } from '../../nodeCatalog';
import { useTranslation } from 'react-i18next';

// 표시 이름은 typeLabelOf(type) — 번역 키 한 곳에서 정한다
interface NodeType {
  type: string;
  category: string;
  instances: any[];
  icon: React.ReactNode;
}

const baseNodeTypes: NodeType[] = [
  {
    type: 'csvData',
    category: 'data',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
      </svg>
    ),
  },
  {
    type: 'rdbData',
    category: 'data',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
      </svg>
    ),
  },
  {
    type: 'transformLoad',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M4 7h11" />
        <path d="M12 4l3 3-3 3" />
        <path d="M20 17H9" />
        <path d="M12 14l-3 3 3 3" />
      </svg>
    ),
  },
  {
    type: 'filter',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
      </svg>
    ),
  },
  {
    type: 'join',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M8 12h8"></path>
        <path d="M12 8v8"></path>
      </svg>
    ),
  },
  {
    type: 'branch',
    category: 'control',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M16 3h5v5" />
        <path d="M4 20L21 3" />
        <path d="M21 16v5h-5" />
        <path d="M15 15l6 6" />
        <path d="M4 4l5 5" />
      </svg>
    ),
  },
  {
    type: 'merge',
    category: 'control',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M8 3H3v5" />
        <path d="M3 8l5 5" />
        <path d="M21 3h-5v5" />
        <path d="M16 8l5-5" />
        <path d="M12 20v-8" />
        <path d="M15 12h-6" />
      </svg>
    ),
  },
  {
    type: 'userConfirm',
    category: 'control',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
    ),
  },
  {
    type: 'runWorkflow',
    category: 'exec',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
    ),
  },
  {
    type: 'shellCommand',
    category: 'exec',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 9l3 3-3 3" /><path d="M12 15h5" /></svg>
    ),
  },
  {
    type: 'sqlScript',
    category: 'exec',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" /><path d="M9 11l-2 2 2 2M15 11l2 2-2 2" /></svg>
    ),
  },
  {
    type: 'aggregate',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M18 4H6l6 8-6 8h12" /></svg>
    ),
  },
  {
    type: 'sort',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M4 6h16M4 12h10M4 18h5" /><path d="M18 10v8m0 0l-3-3m3 3l3-3" /></svg>
    ),
  },
  {
    type: 'lookup',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="10" cy="10" r="6" /><path d="M20 20l-5.5-5.5" /></svg>
    ),
  },
  {
    // 데이터비교 — 두 데이터를 키로 짝지어 값 수준 비교, 결과는 SAM 레포트 (2026-10-20)
    type: 'compare',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="5" width="7" height="14" rx="1" /><rect x="14" y="5" width="7" height="14" rx="1" /><path d="M10 9h4M10 15h4" strokeDasharray="1.5 1.5" /></svg>
    ),
  },
  {
    type: 'quality',
    category: 'process',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="M9 12l2 2 4-4" /></svg>
    ),
  },
  {
    type: 'subflow',
    category: 'group',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="4" y="4" width="16" height="16" rx="4" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    ),
  },
  {
    type: 'repeatGroup',
    category: 'group',
    instances: [],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="4" y="4" width="16" height="16" rx="4" />
        <circle cx="12" cy="12" r="4" />
        <path d="M8 20c0-2.21 3.58-4 8-4" stroke="currentColor" />
      </svg>
    ),
  },
];

// 노드 타입별 3자리 약어 매핑
const ToolInstancePanel: React.FC = () => {
  const { t } = useTranslation();
  const selectedClass = useSelector((state: RootState) => state.workflow.selectedNodeClass);
  const instanceNodes = useSelector((state: RootState) => state.workflow.instanceNodes || []);

  // 선택된 클래스와 일치하는 인스턴스만 필터링
  const filteredInstances = selectedClass
    ? instanceNodes.filter((inst) => inst.type === selectedClass)
    : [];

  // 보여 줄 것이 없으면 자리를 차지하지 않는다 — 예전에는 128px 를 항상 잡고 "아직 없음"만 보였다.
  // 툴박스에서 클래스를 고르고, 그 클래스에 등록된 노드 인스턴스가 있을 때만 캔버스 위로 펼쳐진다.
  if (!selectedClass || filteredInstances.length === 0) return null;

  const classLabel = typeLabelOf(selectedClass || '');

  return (
    <div className="flex flex-row items-center gap-3 border-b border-gray-200 bg-white w-full px-2 py-1.5 overflow-x-auto shrink-0">
      <span className="text-[0.6875rem] text-gray-500 shrink-0">{t('workflow:toolbox.instances', { name: classLabel })}</span>
      {filteredInstances.map((instance, idx) => (
        <div
          key={instance.id || idx}
          className={classNames(
            'flex items-center px-2 py-1 border bg-white cursor-move transition-all shrink-0',
            shapeClassOf(instance.type),          // 캔버스와 같은 도형 (nodeCatalog 가 정한다)
            'border-gray-200 hover:border-blue-500 hover:shadow-sm'
          )}
          draggable
          onDragStart={(e) => {
            // 캔버스의 onDrop 이 읽는 형식 그대로 — 새 노드로 추가된다 (id 는 캔버스가 새로 만든다)
            const targetRect = e.currentTarget.getBoundingClientRect();
            e.dataTransfer.setData('application/reactflow-node', JSON.stringify({
              type: instance.type,
              data: { label: instance.label, config: { ...instance.config, label: instance.label } },
              offsetX: e.clientX - targetRect.left,
              offsetY: e.clientY - targetRect.top,
              [GHOST_CENTERED]: true,
            }));
            e.dataTransfer.effectAllowed = 'move';
            setNodeDragImage(e, instance.type, instance.label, e.currentTarget);   // 캔버스에 놓일 노드 모양으로 (utils/dragGhost)
          }}
        >
          <div className="w-7 h-7 flex items-center justify-center text-gray-600">
            {instance.icon}
          </div>
          <span className="ml-1.5 text-xs text-gray-700">{instance.label}</span>
        </div>
      ))}
    </div>
  );
};

export default ToolInstancePanel;
export { baseNodeTypes }; 